'use client';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useRouter } from 'next/navigation';
import { Eye, EyeOff, Loader2, ShieldAlert } from 'lucide-react';

interface AdminFormData {
  adminId: string;
  password: string;
  securityKey: string;
}

export default function AdminLoginForm() {
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const router = useRouter();

  const { register, handleSubmit, formState: { errors } } = useForm<AdminFormData>();

  const onSubmit = async (_data: AdminFormData) => {
    setLoading(true);
    // BACKEND INTEGRATION: POST /api/auth/admin — verify adminId, password, securityKey
    await new Promise((r) => setTimeout(r, 1400));
    setLoading(false);
    router.push('/parent-dashboard');
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-5" noValidate>
      <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
        <ShieldAlert size={16} className="text-red-500 shrink-0 mt-0.5" />
        <p className="text-xs text-red-600">Administrator access is restricted. All login attempts are logged and monitored.</p>
      </div>

      <div>
        <label htmlFor="admin-id" className="block text-sm font-semibold text-foreground mb-1.5">Admin ID</label>
        <input
          id="admin-id"
          type="text"
          {...register('adminId', { required: 'Admin ID is required' })}
          placeholder="ADMIN-2026-XXXX"
          className="w-full px-4 py-2.5 bg-input border border-border rounded-lg text-sm text-foreground font-mono placeholder:text-muted-foreground outline-none focus:ring-2 focus:ring-ring transition-all"
        />
        {errors.adminId && <p className="text-xs text-red-500 mt-1">{errors.adminId.message}</p>}
      </div>

      <div>
        <label htmlFor="admin-password" className="block text-sm font-semibold text-foreground mb-1.5">Password</label>
        <div className="relative">
          <input
            id="admin-password"
            type={showPassword ? 'text' : 'password'}
            {...register('password', { required: 'Password is required' })}
            placeholder="••••••••"
            className="w-full px-4 py-2.5 pr-10 bg-input border border-border rounded-lg text-sm text-foreground placeholder:text-muted-foreground outline-none focus:ring-2 focus:ring-ring transition-all"
          />
          <button type="button" onClick={() => setShowPassword((p) => !p)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground" aria-label="Toggle password">
            {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
          </button>
        </div>
        {errors.password && <p className="text-xs text-red-500 mt-1">{errors.password.message}</p>}
      </div>

      <div>
        <label htmlFor="admin-key" className="block text-sm font-semibold text-foreground mb-1.5">Security Key</label>
        <p className="text-xs text-muted-foreground mb-2">The hardware or software security key issued to your administrator account.</p>
        <input
          id="admin-key"
          type="text"
          {...register('securityKey', { required: 'Security key is required' })}
          placeholder="SK-XXXXXXXX-XXXX"
          className="w-full px-4 py-2.5 bg-input border border-border rounded-lg text-sm text-foreground font-mono placeholder:text-muted-foreground outline-none focus:ring-2 focus:ring-ring transition-all"
        />
        {errors.securityKey && <p className="text-xs text-red-500 mt-1">{errors.securityKey.message}</p>}
      </div>

      <button
        type="submit"
        disabled={loading}
        className="w-full flex items-center justify-center gap-2 py-3 bg-slate-800 text-white font-semibold rounded-lg btn-scale hover:bg-slate-900 transition-all disabled:opacity-60"
        style={{ minHeight: '44px' }}
      >
        {loading ? <Loader2 size={16} className="animate-spin" /> : null}
        {loading ? 'Authenticating...' : 'Access Control Center'}
      </button>
    </form>
  );
}